import Decimal from "decimal.js";
import * as XLSX from "xlsx";

// ターゲット行の定義 (他の関数でも参照可能にする)
// 設備投資システムでは、P&L画面で開発費を別途入力するため
// 工場販管費は「S, G&A Expenses」ではなく
// 「Individual sales, general, and administrative expenses」を参照する
export const TARGET_ROWS = [
  { key: "NetSales", title: "Sales" },                               // 売上合計
  { key: "Material", title: "Purchase of raw materials" },           // 材料費
  { key: "OutsideProcess", title: "Outside process" },               // 外注費
  { key: "Labor", title: "Salaries" },                               // 労務費
  { key: "FactoryOverhead", title: "Factory expenses" },             // OVERHEAD
  { key: "Depreciation", title: "Depreciation and amortization" },   // 減価償却
  { key: "FactorySGA", title: "Individual sales, general, and administrative expenses" }, // 工場販管費
];


// 月名 → 数字へのマッピング
export const MONTH_MAP: Record<string, string> = {
  Jan: "01",
  Feb: "02",
  Mar: "03",
  Apr: "04",
  May: "05",
  Jun: "06",
  Jul: "07",
  Aug: "08",
  Sep: "09",
  Oct: "10",
  Nov: "11",
  Dec: "12",
};

// 戻り値の型定義
export interface ParsedPAndLResult {
  fiscalSums: Record<string, Record<string, number>>;
  monthlyData: Record<string, Record<string, number>>;
  lcValue: string;
  valueDescription: string;
  fiscalYears: string[];
  plTitle: string;
}

/**
 * P&Lファイルを読み込んで解析する関数
 * @param fileData Base64エンコードされたExcelファイルデータ
 * @returns 解析した結果
 */
export const parsePAndLFile = (fileData: string): ParsedPAndLResult => {
  const fiscalSums: Record<string, Record<string, number>> = {};
  const monthlyData: Record<string, Record<string, number>> = {};
  let lcValue = "";
  let valueDescription = "";
  const fiscalYears: string[] = [];
  let plTitle = "";
  let workbook: XLSX.WorkBook;

  if (!fileData) {
    throw new Error("File data is empty.");
  }

  try {
    // Base64 データからコンテンツ部分を抽出しバイナリ化
    const base64Content = fileData.split(",")[1];
    const binaryString = atob(base64Content);
    const binaryLen = binaryString.length;
    const bytes = new Uint8Array(binaryLen);
    for (let i = 0; i < binaryLen; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Excelファイルをパース
    workbook = XLSX.read(bytes, { type: "array" });

    // 最初のシートからA2セル情報を取得
    if (workbook.SheetNames.length > 0) {
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const firstSheetRows: string[][] = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
      if (firstSheetRows.length >= 2 && firstSheetRows[1] && firstSheetRows[1][0]) {
        plTitle = String(firstSheetRows[1][0]).trim();
      }
    }

    // シートを繰り返し処理
    workbook.SheetNames.forEach((sheetName) => {
      const worksheet = workbook.Sheets[sheetName];
      const rows: string[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      let fiscalYearForSheet: string | null = null; // シートごとの年度
      // フォーマットチェックフラグ
      let lcValueFound = false;
      let value1Found  = false;
      let actualFyFound = false;
      let totalIndex = -1;

      // シートのヘッダー行からすべての月列が存在するかチェック
      if (rows.length > 0) {
        // ヘッダー行を取得
        const headerRow = rows[0] as string[];
        // ヘッダー行にTotal列存在チェック
        totalIndex = headerRow.indexOf("Total");
        if (totalIndex === -1) {
          throw new Error(`P&L file format error in sheet "${sheetName}": Missing "Total" column in header row.`);
        }

        // 月列の存在チェック
        const missingMonths = Object.keys(MONTH_MAP).filter((monthName) => !headerRow.includes(monthName));
        if (missingMonths.length > 0) {
          throw new Error(`P&L file format error in sheet "${sheetName}": Missing month columns: ${missingMonths.join(", ")}`);
        }
      }

      // LC = XXX の検証
      const lcRowIndex = rows.findIndex((row: any) => {
        const nColumnValue = row[13]; // N列
        return typeof nColumnValue === "string" && nColumnValue.startsWith("LC = ");
      });
      if (lcRowIndex === -1) {
        throw new Error('P&L file format error: Missing "LC = XXX" row.');
      } else {
        const nColumnValue = rows[lcRowIndex][13];
        lcValue = nColumnValue.split("LC = ")[1]?.trim() || "";
      }

      // "Value 1 of this row means" の検証
      const value1RowIndex = rows.findIndex((row: any) => {
        const firstCell = row[0];
        return typeof firstCell === "string" && firstCell.includes("Value 1 of this row means");
      });
      if (value1RowIndex === -1) {
        throw new Error('P&L file format error: Missing "Value 1 of this row means" row.');
      } else {
        const nColumnValue = rows[value1RowIndex][13];
        valueDescription = nColumnValue ? String(nColumnValue).trim() : "";
      }

      // Actual FY{数字} の年度検証
      const actualFyRowIndex = rows.findIndex((row: any) => {
        const firstCell = row[0];
        return typeof firstCell === "string" && /Actual FY\d{4}/.test(firstCell.trim());
      });
      if (actualFyRowIndex === -1) {
        throw new Error('P&L file format error: Missing "Actual FY{year}" row.');
      } else {
        fiscalYearForSheet = rows[actualFyRowIndex][0].match(/\d{4}/)?.[0] || null;
        if (fiscalYearForSheet && !fiscalYears.includes(fiscalYearForSheet)) {
          fiscalYears.push(fiscalYearForSheet);
        }
      }
      // 「Actual FY」以降のターゲット行
      const filteredRows = rows.slice(actualFyRowIndex + 1);

      // TARGET_ROWS の検証 (filteredRows のみを対象)
      const existingTitles = new Set(filteredRows.map((row: any) => row[0]));
      const missingTargets = TARGET_ROWS.filter((target) => !existingTitles.has(target.title));
      if (missingTargets.length > 0) {
        const missingTitles = missingTargets.map((target) => target.title).join(", ");
        throw new Error(`P&L file format error in sheet "${sheetName}": Missing TARGET_ROWS: ${missingTitles}`);
      }

      filteredRows.forEach((row: any) => {
        const firstCell = row[0]; // A列 (インデックス 0)

        // ターゲット行の検証
        TARGET_ROWS.forEach((target) => {
          if (typeof firstCell === "string" && firstCell.startsWith(target.title)) {
            const headerRow = rows[0] as string[];
            const totalRaw = row[totalIndex] != null ? String(row[totalIndex]).replace(/,/g, "") : "";
            const totalValue = new Decimal(totalRaw || "0").toNumber();

            if (fiscalYearForSheet) {
              if (!fiscalSums[fiscalYearForSheet]) fiscalSums[fiscalYearForSheet] = {};
              fiscalSums[fiscalYearForSheet][target.key] = totalValue;

              // 月ごとのデータ取得
              Object.keys(MONTH_MAP).forEach((monthName) => {
                const monthIndex = headerRow.indexOf(monthName);
                if (monthIndex !== -1) {
                  const monthRaw = row[monthIndex] != null ? String(row[monthIndex]).replace(/,/g, "") : "";
                  const monthValue = new Decimal(monthRaw || "0").toNumber();
                  const monthCode = MONTH_MAP[monthName];
                  const yyyymm = ["01", "02", "03"].includes(monthCode)
                    ? `${parseInt(fiscalYearForSheet!, 10) + 1}${monthCode}` // Jan, Feb, Mar は次年
                    : `${fiscalYearForSheet}${monthCode}`; // Apr-Dec は同年

                  if (!monthlyData[yyyymm]) monthlyData[yyyymm] = {};
                  monthlyData[yyyymm][target.key] = monthValue;
                }
              });
            }
          }
        });
      });
    });

    return {
      fiscalSums,
      monthlyData,
      lcValue,
      valueDescription,
      fiscalYears,
      plTitle,
    };
  } catch (error) {
    throw new Error(`Error parsing P&L file: ${error instanceof Error ? error.message : String(error)}`);
  }
};